import { use, useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useUrl } from 'crossroad';
import { PassageDataContext } from '@/data/passage/passage-data-context';
import { LiteraryFeaturesDataContext } from '@/data/literary-features/literary-feature-data-context';
import { useLiteraryFeaturesByPassageData } from '@/data/literary-features/literary-features-by-passage-data-context';
import { parseRef, rangesOverlap, formatFullRefAsLink } from '@/lib/utils/reference-utils';
import { Skeleton } from '@/components/ui/skeleton';
import { SourceLinks } from '@/components/ui/source-links';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { LiteraryFeatureCrossReferenceModal } from '@/components/literary-features/LiteraryFeatureCrossReferenceModal';
import { HorizontalContextNavigator } from './HorizontalContextNavigator';
import { ScripturalConnectionLines } from './ScripturalConnectionLines';
import { DiscoveryCard } from './discovery-card';
import { cn } from '@/lib/utils';
import { ContextHero } from '../context/ContextHero';
import { normalizeBookName, calculateBiblePositionPercent, getCanonicalBookName } from '@/lib/utils/bible-utils';
import { parseCrossReferences } from '@/lib/utils/crossref-utils';
import { BIBLE_BOOKS_MAP, BIBLE_CODE_TO_BOOK, type BibleBook } from '@/lib/utils/bible-utils';
import { getSectionBackgroundColor } from '@/lib/utils/scripture-section-colors';
import {
    BookMarked,
    Quote,
    ArrowRight,
    Library,
    Sprout,
    Sparkles
} from 'lucide-react';
import { AIDisclaimerBadge } from '../ui/ai-disclaimer-badge';
import { FootnoteDataContext } from '@/data/footnotes/footnote-data-context';

// Hero illustration
import scriptureHero from '@/assets/heroes/canonical_interweaving_hero_v2.png';

interface Props {
    passageId: string;
    isTransitionAnimating?: boolean;
}

function LoadingSkeleton() {
    return (
        <div className="space-y-4 p-4">
            <div className="h-40 rounded-xl bg-muted animate-pulse mb-6" />
            <div className="space-y-6">
                {[1, 2, 3].map(i => (
                    <div key={i} className="flex gap-4">
                        <div className="w-0.5 bg-muted h-20" />
                        <div className="flex-1 space-y-2">
                            <Skeleton className="h-6 w-48" />
                            <Skeleton className="h-20 w-full" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function EmptyState() {
    return (
        <div className="space-y-4 p-4">
            <div className="rounded-xl border border-dashed p-12 flex flex-col items-center justify-center text-center space-y-3">
                <Quote className="h-10 w-10 text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">
                    No scriptural context was found for this specific passage.
                </p>
            </div>
        </div>
    );
}

// Helper to get book and section from ref string
function getBookSectionFromRef(ref: string) {
    if (!ref) return null;

    // Attempt to match "Book Chapter:Verse" or "Book Chapter"
    const match = ref.match(/^(.+?)\s+([\d:,-]+)$/);
    if (!match) return null;

    const bookLabel = match[1];
    let bookName: BibleBook | undefined;

    // Check if it's a full book name
    if (BIBLE_BOOKS_MAP.has(bookLabel as BibleBook)) {
        bookName = bookLabel as BibleBook;
    } else {
        // Check if it's a short code
        bookName = BIBLE_CODE_TO_BOOK[bookLabel];
    }

    if (bookName) {
        return BIBLE_BOOKS_MAP.get(bookName)?.section;
    }
    return null;
}

export default function PassageScripturalContext({ passageId, isTransitionAnimating }: Props) {
    const passageContext = use(PassageDataContext);
    const literaryFeaturesContext = use(LiteraryFeaturesDataContext);
    const { data: passageFeatures, isLoading: featuresLoading } = useLiteraryFeaturesByPassageData(passageId);
    const isDarkMode = useDarkMode();
    const [, setUrl] = useUrl();
    const footnoteContext = use(FootnoteDataContext);
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['allusions-passage', 'synoptic-passage', 'corpus-context', 'canonical-context', 'authorial-context']));
    // Initialize with all allusion items expanded by default
    const [expandedItems, setExpandedItems] = useState<Set<string>>(() => {
        const initialExpanded = new Set<string>();
        // Will be populated when allusions are rendered
        return initialExpanded;
    });

    // State for cross-reference modal
    const [crossRefModal, setCrossRefModal] = useState<{
        type: 'theme';
        value: string;
    } | null>(null);

    const [showAllCrossRefs, setShowAllCrossRefs] = useState(false);

    const containerRef = useRef<HTMLDivElement>(null);

    const passage = useMemo(() => {
        return passageContext.data?.items.find(p => p.id === passageId);
    }, [passageContext.data, passageId]);

    const bookFeatures = useMemo(() => {
        if (!literaryFeaturesContext || !passage) {
            return null;
        }
        return literaryFeaturesContext.getLiteraryFeaturesByBook(passage.book);
    }, [literaryFeaturesContext, passage]);

    const toggleSection = useCallback((section: string) => {
        setExpandedSections(prev => {
            const next = new Set(prev);
            if (next.has(section)) {
                next.delete(section);
            } else {
                next.add(section);
            }
            return next;
        });
    }, []);

    const toggleItemExpansion = useCallback((itemId: string) => {
        setExpandedItems(prev => {
            const next = new Set(prev);
            if (next.has(itemId)) {
                next.delete(itemId);
            } else {
                next.add(itemId);
            }
            return next;
        });
    }, []);

    const scrollToAllusion = useCallback((itemId: string) => {
        const element = document.getElementById(itemId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Pulse effect to highlight
            element.classList.add('ring-2', 'ring-primary', 'transition-all');
            setTimeout(() => {
                element.classList.remove('ring-2', 'ring-primary');
            }, 2000);
        }
    }, []);

    // Combine and sort allusions and intertextual arcs
    const sortedAllusionsAndArcs = useMemo(() => {
        const items: Array<any> = [];

        // Add allusions
        if (passageFeatures?.allusions) {
            passageFeatures.allusions.forEach(allusion => {
                let sortKey = 0;
                if (allusion.ref) {
                    const range = parseRef(allusion.ref);
                    if (range) {
                        sortKey = range.chapterStart * 1000 + range.verseStart;
                    }
                }
                items.push({ ...allusion, sortKey });
            });
        }

        // Add filtered intertextual arcs
        if (bookFeatures?.intertextualArcs && passage) {
            const passageRange = parseRef(passage.ref);
            if (passageRange) {
                bookFeatures.intertextualArcs.forEach(arc => {
                    const sourceMatch = arc.source.match(/^(.+?)\s+([\d:,-]+)$/);
                    const sourceBook = sourceMatch ? sourceMatch[1] : null;
                    const sourceRef = sourceMatch ? sourceMatch[2] : null;

                    const targetMatch = arc.target.match(/^(.+?)\s+([\d:,-]+)$/);
                    const targetBook = targetMatch ? targetMatch[1] : null;
                    const targetRef = targetMatch ? targetMatch[2] : null;

                    let shouldInclude = false;
                    let sortKey = 0;
                    let localRef = '';

                    if (sourceBook === passage.book && sourceRef) {
                        const sourceRange = parseRef(sourceRef);
                        if (sourceRange && rangesOverlap(passageRange, sourceRange)) {
                            shouldInclude = true;
                            sortKey = sourceRange.chapterStart * 1000 + sourceRange.verseStart;
                            localRef = sourceRef;
                        }
                    }

                    if (!shouldInclude && targetBook === passage.book && targetRef) {
                        const targetRange = parseRef(targetRef);
                        if (targetRange && rangesOverlap(passageRange, targetRange)) {
                            shouldInclude = true;
                            sortKey = targetRange.chapterStart * 1000 + targetRange.verseStart;
                            localRef = targetRef;
                        }
                    }

                    if (shouldInclude) {
                        items.push({
                            ref: localRef,
                            type: arc.type || 'Canonical Connection',
                            sourceRef: arc.target,
                            text: '',
                            explanation: arc.note || '',
                            importance: arc.importance,
                            sources: arc.sources,
                            sortKey
                        });
                    }
                });
            }
        }

        return items.sort((a, b) => {
            // Sort by Bible position (TO context)
            const matchA = a.sourceRef?.match(/^(.+?)\s+(\d+)/);
            const matchB = b.sourceRef?.match(/^(.+?)\s+(\d+)/);

            if (matchA && matchB) {
                const bookA = matchA[1] as BibleBook;
                const chapA = parseInt(matchA[2], 10);
                const bookB = matchB[1] as BibleBook;
                const chapB = parseInt(matchB[2], 10);

                const posA = calculateBiblePositionPercent(bookA, chapA);
                const posB = calculateBiblePositionPercent(bookB, chapB);

                if (posA !== posB) return posA - posB;
            }

            // Fallback to local passage position
            return a.sortKey - b.sortKey;
        });
    }, [passageFeatures?.allusions, bookFeatures?.intertextualArcs, passage]);

    // Get cross-references for this passage
    const flattenedCrossReferences = useMemo(() => {
        if (!footnoteContext || !passage) return [];
        const rawFootnotes = footnoteContext.getCrossReferencesForPassage(passage.book, passage.ref);
        return parseCrossReferences(rawFootnotes, passage.book, passage.ref);
    }, [footnoteContext, passage]);

    const connectionPoints = useMemo(() => {
        const points: { id: string; ref: string; percent: number; type: 'allusion' | 'crossref' }[] = [];

        // Allusions and Arcs
        sortedAllusionsAndArcs.forEach((allusion, index) => {
            const match = allusion.sourceRef?.match(/^(.+?)\s+(\d+)/);
            if (!match) return;

            const bookName = match[1] as BibleBook;
            const chapter = parseInt(match[2], 10);
            if (!BIBLE_BOOKS_MAP.has(bookName)) return;

            const percent = calculateBiblePositionPercent(bookName, chapter);
            points.push({
                id: `allusion-${index}`,
                ref: allusion.sourceRef,
                percent,
                type: 'allusion'
            });
        });

        // Cross-References
        flattenedCrossReferences.forEach((crossRef, index) => {
            const match = crossRef.targetRef.match(/^(.+?)\s+(\d+)/);
            if (match) {
                const bookName = getCanonicalBookName(match[1]) as BibleBook;
                const chapter = parseInt(match[2], 10);
                const percent = calculateBiblePositionPercent(bookName, chapter);

                points.push({
                    id: `crossref-${index}`,
                    ref: crossRef.targetRef,
                    percent,
                    type: 'crossref'
                });
            }
        });

        return points;
    }, [sortedAllusionsAndArcs, flattenedCrossReferences]);

    // Auto-expand everything on mount or passage change
    useEffect(() => {
        if (!featuresLoading && passageFeatures) {
            // Expand all sections
            setExpandedSections(prev => {
                const next = new Set(prev);
                next.add('corpus-context');
                next.add('canonical-context');
                next.add('authorial-context');
                return next;
            });

            // Expand all individual items (allusions and parallels)
            const allusionIds = sortedAllusionsAndArcs.map((_, i) => `allusion-${i}`);
            const parallelIds = (passageFeatures?.synopticParallels || []).map((_, i) => `parallel-${i}`);
            setExpandedItems(new Set([...allusionIds, ...parallelIds]));
        }
    }, [passageId, featuresLoading, passageFeatures, sortedAllusionsAndArcs]);

    if (featuresLoading || !passageContext.data || !literaryFeaturesContext) {
        return <LoadingSkeleton />;
    }

    if (!passage || !passageFeatures) {
        return <EmptyState />;
    }

    return (
        <div className="relative" ref={containerRef}>

            <div className="relative flex flex-col min-h-full">
                {/* Cinematic Hero Card */}
                <ContextHero
                    eyebrow="SCRIPTURAL CONTEXT"
                    headline="Scriptural Connections"
                    subtitle="Trace the intricate web of cross-references, echoes, and fulfillments that connect this passage to the whole Bible."
                    image={scriptureHero}
                    onClose={() => setUrl(`/app/passage/${passageId}`)}
                    showClose={false}
                />

                {/* Canon Navigator - Positioned as a divider underneath the hero */}
                <HorizontalContextNavigator
                    mode="bible"
                    passage={passage}
                    allPassages={passageContext.data?.items || []}
                    setUrl={setUrl}
                    sticky={true}
                    connectionPoints={connectionPoints}
                    onMarkerClick={scrollToAllusion}
                    className="z-40"
                    isTransitionAnimating={isTransitionAnimating}
                />

                <div className="px-6 pt-4 pb-20">


                    {/* Allusions and Canonical Connections */}
                    {sortedAllusionsAndArcs.length > 0 && (
                        <div className="mt-8 space-y-6 relative">
                            {/* Section Header */}
                            <div className="space-y-1">
                                <h2 className="text-2xl font-serif font-semibold tracking-tight text-foreground/90">
                                    Allusions and Echoes
                                </h2>
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/60">
                                        {sortedAllusionsAndArcs.length} Scriptural Connection{sortedAllusionsAndArcs.length !== 1 ? 's' : ''}
                                    </span>
                                    <div className="h-px w-8 bg-primary/20" />
                                </div>
                            </div>

                            {/* Individual Allusion Cards */}
                            <div className="space-y-4">
                                {sortedAllusionsAndArcs.map((allusion, index) => {
                                    const section = getBookSectionFromRef(allusion.sourceRef);
                                    const sectionColor = section ? getSectionBackgroundColor(section, isDarkMode, true) : 'hsl(var(--primary))';
                                    const itemId = `allusion-${index}`;

                                    const getAllusionTypeTooltip = (type: string) => {
                                        switch (type) {
                                            case 'Direct Quote': return 'An explicit, formal reference to an earlier text.';
                                            case 'Allusion': return 'A non-formal invocation of an earlier text.';
                                            case 'Echo': return 'Subtle resonances of Scriptural texts.';
                                            case 'Typology': return 'Type-antitype relationship.';
                                            case 'Canonical Connection': return 'Intertextual connection between biblical passages.';
                                            default: return type;
                                        }
                                    };

                                    // Build card title from the reference flow
                                    const cardTitle = (
                                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                                            {/* Mobile: Header Row (From -> To) */}
                                            <div className="flex items-center gap-1.5 sm:gap-3 min-w-0">
                                                {/* From: Local verse reference */}
                                                <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                                                    <span className="hidden sm:inline text-[8px] sm:text-[9px] font-bold uppercase tracking-widest text-muted-foreground/40">From</span>
                                                    <span className="text-[10px] sm:text-xs font-mono font-semibold text-foreground/80 bg-muted/30 px-1.5 sm:px-2 py-0.5 rounded whitespace-nowrap">
                                                        {allusion.ref || 'This passage'}
                                                    </span>
                                                </div>

                                                {/* Arrow separator */}
                                                <ArrowRight className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-primary/40 shrink-0" />

                                                {/* To: External passage reference */}
                                                <div className="flex items-center gap-1 sm:gap-2 min-w-0">
                                                    <span className="hidden sm:inline text-[8px] sm:text-[9px] font-bold uppercase tracking-widest text-muted-foreground/40 shrink-0">To</span>
                                                    <div className="text-sm sm:text-base font-serif font-medium text-foreground/90 truncate">
                                                        {formatFullRefAsLink(allusion.sourceRef, isDarkMode, setUrl)}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Reference Type Badge (Separate line on mobile) */}
                                            <div className="flex items-center gap-3">
                                                <TooltipProvider>
                                                    <Tooltip>
                                                        <TooltipTrigger>
                                                            <Badge
                                                                className={cn(
                                                                    "text-[8px] sm:text-[9px] font-bold uppercase tracking-widest cursor-help border-2 transition-all hover:scale-105 whitespace-nowrap shrink-0",
                                                                    sectionColor === '#10b981' && `bg-emerald-500/10 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'} border-emerald-500/30`,
                                                                    sectionColor === '#f59e0b' && `bg-amber-500/10 ${isDarkMode ? 'text-amber-400' : 'text-amber-700'} border-amber-500/30`,
                                                                    sectionColor === '#8b5cf6' && `bg-violet-500/10 ${isDarkMode ? 'text-violet-400' : 'text-violet-700'} border-violet-500/30`
                                                                )}
                                                            >
                                                                {allusion.type}
                                                            </Badge>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <p className="text-xs">{getAllusionTypeTooltip(allusion.type)}</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>

                                                {/* Importance badge */}
                                                {/* {allusion.importance && (
                                                <ImportanceBadge
                                                    importance={allusion.importance}
                                                    context="general"
                                                    className="sm:ml-auto"
                                                />
                                            )} */}
                                            </div>
                                        </div>
                                    );

                                    return (
                                        <TooltipProvider key={index}>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <div>
                                                        <DiscoveryCard
                                                            id={itemId}
                                                            isExpanded={expandedItems.has(itemId)}
                                                            onToggle={() => toggleItemExpansion(itemId)}
                                                            header={cardTitle}
                                                            borderColor={sectionColor}
                                                            markerNumber={index + 1}
                                                            className="allusion-card"
                                                            data-passage-ref={allusion.sourceRef}
                                                        >
                                                            <div className="space-y-3">
                                                                {allusion.text && (
                                                                    <div className="relative pl-4">
                                                                        <Quote className="absolute left-0 top-0 h-3 w-3 text-primary/20" />
                                                                        <p className="text-sm leading-relaxed italic text-foreground/80 font-light">
                                                                            {allusion.text}
                                                                        </p>
                                                                    </div>
                                                                )}

                                                                {allusion.explanation && (
                                                                    <p className="text-sm leading-relaxed text-muted-foreground font-light">
                                                                        {allusion.explanation}
                                                                    </p>
                                                                )}

                                                                {allusion.sources?.length > 0 && (
                                                                    <div className="pt-2 border-t border-border/10">
                                                                        <SourceLinks sources={allusion.sources} />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </DiscoveryCard>
                                                    </div>
                                                </TooltipTrigger>
                                                <TooltipContent side="left">
                                                    <p className="text-xs font-semibold">{section || 'Scripture'}</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    );
                                })}

                                {flattenedCrossReferences.length > 10 && !showAllCrossRefs && (
                                    <button
                                        key="show-all-crossrefs-btn"
                                        onClick={() => setShowAllCrossRefs(true)}
                                        className="w-full py-4 rounded-xl border border-dashed border-primary/20 text-primary/60 hover:text-primary hover:border-primary/40 hover:bg-primary/5 transition-all font-serif text-sm flex flex-col items-center justify-center gap-1 group/more"
                                    >
                                        <div className="flex items-center gap-2">
                                            <Sparkles className="h-4 w-4 group-hover/more:scale-110 transition-transform" />
                                            <span>Show All {flattenedCrossReferences.length} Cross-References</span>
                                        </div>
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Cross-References */}
                    {flattenedCrossReferences.length > 0 && (
                        <div className="mt-16 space-y-6 relative">
                            {/* Section Header */}
                            <div className="space-y-1">
                                <h2 className="text-2xl font-serif font-semibold tracking-tight text-foreground/90">
                                    Cross-References
                                </h2>
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/60">
                                        {flattenedCrossReferences.length} Cross-Reference{flattenedCrossReferences.length !== 1 ? 's' : ''}
                                    </span>
                                    <div className="h-px w-8 bg-primary/20" />
                                </div>
                            </div>

                            {/* Individual Cross-Reference Cards */}
                            <div className="space-y-4">
                                {(showAllCrossRefs ? flattenedCrossReferences : flattenedCrossReferences.slice(0, 10)).map((crossRef, index) => {
                                    const itemId = `crossref-${index}`;

                                    const cardHeader = (
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 w-full">
                                            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                                                {/* From: Local verse */}
                                                <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                                                    <span className="text-[8px] sm:text-[9px] font-bold uppercase tracking-widest text-muted-foreground/40">From</span>
                                                    <span className="text-[10px] sm:text-xs font-mono font-semibold text-foreground/80 bg-muted/30 px-1.5 sm:px-2 py-0.5 rounded whitespace-nowrap">
                                                        {crossRef.ref}
                                                    </span>
                                                </div>

                                                {/* Arrow separator */}
                                                <ArrowRight className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-primary/40 shrink-0" />

                                                {/* To: Target passage reference */}
                                                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                                                    <span className="hidden sm:inline text-[8px] sm:text-[9px] font-bold uppercase tracking-widest text-muted-foreground/40 shrink-0">To</span>
                                                    <div className="text-sm sm:text-base font-serif font-medium text-foreground/90 truncate">
                                                        {formatFullRefAsLink(crossRef.targetRef, isDarkMode, setUrl)}
                                                    </div>

                                                    {/* Inline Badge */}
                                                    <Badge
                                                        className={cn(
                                                            "text-[7px] sm:text-[8px] px-1.5 py-0 font-bold uppercase tracking-widest border transition-all hover:scale-105 whitespace-nowrap shrink-0",
                                                            `bg-sky-500/10 ${isDarkMode ? 'text-sky-400' : 'text-sky-700'} border-sky-500/30`
                                                        )}
                                                    >
                                                        Cross-Ref
                                                    </Badge>
                                                </div>
                                            </div>
                                        </div>
                                    );

                                    return (
                                        <DiscoveryCard
                                            key={index}
                                            id={itemId}
                                            isExpanded={expandedItems.has(itemId)}
                                            onToggle={() => toggleItemExpansion(itemId)}
                                            header={cardHeader}
                                            borderColor="#0ea5e9"
                                            markerNumber={index + 1}
                                            className="crossref-card"
                                            data-passage-ref={crossRef.targetRef}
                                            disableBlur
                                        >
                                            {crossRef.footnoteText && (
                                                <p className="text-sm leading-relaxed text-muted-foreground font-light italic pl-4 border-l-2 border-primary/10">
                                                    {crossRef.footnoteText}
                                                </p>
                                            )}
                                        </DiscoveryCard>
                                    );
                                })}

                                {flattenedCrossReferences.length > 10 && !showAllCrossRefs && (
                                    <button
                                        onClick={() => setShowAllCrossRefs(true)}
                                        className="w-full py-3 rounded-xl border border-dashed border-primary/20 text-primary/60 hover:text-primary hover:border-primary/40 hover:bg-primary/5 transition-all font-serif text-sm flex items-center justify-center gap-2"
                                    >
                                        <span>Show All {flattenedCrossReferences.length} Cross-References</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Gospel Parallels */}
                    {passageFeatures?.synopticParallels?.length > 0 && (
                        <div className="mt-16 space-y-6 relative">
                            {/* Section Header */}
                            <div className="space-y-1">
                                <h2 className="text-2xl font-serif font-semibold tracking-tight text-foreground/90">
                                    Gospel Parallels
                                </h2>
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/60">
                                        {passageFeatures.synopticParallels.length} Synoptic Parallel{passageFeatures.synopticParallels.length !== 1 ? 's' : ''}
                                    </span>
                                    <div className="h-px w-8 bg-primary/20" />
                                </div>
                            </div>

                            {/* Individual Parallel Cards */}
                            <div className="space-y-4">
                                {passageFeatures.synopticParallels.map((parallel, index) => {
                                    const itemId = `parallel-${index}`;
                                    const cardTitle = (
                                        <div className="flex flex-wrap gap-2 items-center">
                                            {parallel.parallelRefs.map((ref, i) => (
                                                <Badge
                                                    key={i}
                                                    variant="secondary"
                                                    className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-all font-medium py-1 text-xs"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setUrl(`/${ref}`);
                                                    }}
                                                >
                                                    {ref}
                                                </Badge>
                                            ))}
                                        </div>
                                    );

                                    return (
                                        <DiscoveryCard
                                            key={index}
                                            isExpanded={expandedItems.has(itemId)}
                                            onToggle={() => toggleItemExpansion(itemId)}
                                            header={cardTitle}
                                        >
                                            <div className="space-y-3">
                                                {parallel.similarities && parallel.similarities !== 'N/A' && (
                                                    <div className="p-3 rounded-lg bg-primary/[0.02] border border-primary/5">
                                                        <div className="text-[9px] uppercase tracking-widest text-primary/50 mb-1">Key Similarities</div>
                                                        <p className="text-sm font-light text-foreground/80 leading-relaxed italic">{parallel.similarities}</p>
                                                    </div>
                                                )}

                                                {parallel.differences && (
                                                    <div>
                                                        <div className="text-[9px] uppercase tracking-widest text-muted-foreground/50 mb-1">Distinctive Variations</div>
                                                        <p className="text-sm font-light text-muted-foreground/70 leading-relaxed">{parallel.differences}</p>
                                                    </div>
                                                )}

                                                {parallel.sources?.length > 0 && (
                                                    <div className="pt-2 border-t border-border/10">
                                                        <SourceLinks sources={parallel.sources} />
                                                    </div>
                                                )}
                                            </div>
                                        </DiscoveryCard>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Biblical Themes */}
                    {passage.themes?.length > 0 && (
                        <div className="mt-16 space-y-6 relative">
                            {/* Section Header */}
                            <div className="space-y-1">
                                <h2 className="text-2xl font-serif font-semibold tracking-tight text-foreground/90">
                                    Key Biblical Themes
                                </h2>
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/60">
                                        Explore Themes Found in this Passage
                                    </span>
                                    <div className="h-px w-8 bg-primary/20" />
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-2">
                                {passage.themes.map((theme, index) => (
                                    <Badge
                                        key={index}
                                        variant="outline"
                                        className="px-3 py-1.5 bg-primary/5 hover:bg-primary hover:text-primary-foreground transition-all duration-300 font-serif text-sm border-primary/20 cursor-pointer"
                                        onClick={() => setCrossRefModal({
                                            type: 'theme',
                                            value: theme
                                        })}
                                    >
                                        {theme}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Biblical Context - Canonical Placement */}
                    {(bookFeatures?.biblicalContext?.corpus || bookFeatures?.biblicalContext?.canonical) && (
                        <div className="mt-16 space-y-6 relative">
                            {/* Section Header */}
                            <div className="space-y-1">
                                <h3 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground leading-tight">
                                    How {normalizeBookName(passage.book)} Fits in the Bible
                                </h3>
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/60">
                                        Canonical Placement and Significance
                                    </span>
                                    <div className="h-px w-8 bg-primary/20" />
                                </div>
                            </div>

                            <div className="space-y-4">
                                {bookFeatures?.biblicalContext?.corpus && (
                                    <DiscoveryCard
                                        isExpanded={expandedSections.has('corpus-context')}
                                        onToggle={() => toggleSection('corpus-context')}
                                        title="Corpus Context"
                                        icon={Library}
                                    >
                                        <div className="space-y-3 pt-2">
                                            <h5 className="font-serif text-xl font-medium text-foreground/90">{bookFeatures.biblicalContext.corpus.summary}</h5>
                                            <p className="text-sm font-light leading-relaxed text-foreground/80">{bookFeatures.biblicalContext.corpus.description}</p>
                                        </div>
                                    </DiscoveryCard>
                                )}

                                {bookFeatures?.biblicalContext?.canonical && (
                                    <DiscoveryCard
                                        isExpanded={expandedSections.has('canonical-context')}
                                        onToggle={() => toggleSection('canonical-context')}
                                        title="Canonical Significance"
                                        icon={BookMarked}
                                    >
                                        <div className="space-y-3 pt-2">
                                            <h5 className="font-serif text-xl font-medium text-foreground/90">{bookFeatures.biblicalContext.canonical.summary}</h5>
                                            <p className="text-sm font-light leading-relaxed text-foreground/80">{bookFeatures.biblicalContext.canonical.description}</p>
                                        </div>
                                    </DiscoveryCard>
                                )}

                                {(bookFeatures?.biblicalContext?.author?.development || bookFeatures?.biblicalContext?.author?.comparison) && (
                                    <DiscoveryCard
                                        isExpanded={expandedSections.has('authorial-context')}
                                        onToggle={() => toggleSection('authorial-context')}
                                        title="Authorial Development"
                                        icon={Sprout}
                                    >
                                        <div className="grid sm:grid-cols-2 gap-6 pt-4">
                                            {bookFeatures.biblicalContext.author.development && (
                                                <div className="space-y-2">
                                                    <h6 className="text-[10px] font-bold uppercase tracking-widest text-primary/60">Theological Development</h6>
                                                    <p className="text-xs leading-relaxed italic text-foreground/70">{bookFeatures.biblicalContext.author.development}</p>
                                                </div>
                                            )}
                                            {bookFeatures.biblicalContext.author.comparison && (
                                                <div className="space-y-2">
                                                    <h6 className="text-[10px] font-bold uppercase tracking-widest text-primary/60">Comparative Works</h6>
                                                    <p className="text-xs leading-relaxed italic text-foreground/70">{bookFeatures.biblicalContext.author.comparison}</p>
                                                </div>
                                            )}
                                        </div>
                                    </DiscoveryCard>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* AI Disclaimer */}
                <div className="py-8 border-t border-border/50 flex flex-col items-center gap-4">

                    <AIDisclaimerBadge
                        model={passageFeatures?.generatedBy || (passageFeatures as any)?.source}
                        passageId={passageId}
                        contextType="scriptural-context"
                    />
                    <p className="text-[10px] text-muted-foreground text-center max-w-[400px]">
                        Intertextual analysis synthesized by the BibleGlen Module using Gemini 3.0 Pro. Canonical interweaving optimized for structural and theological mapping.
                    </p>
                </div>

                {/* Cross-Reference Modal */}
                {
                    crossRefModal && (
                        <LiteraryFeatureCrossReferenceModal
                            featureType={crossRefModal.type}
                            featureValue={crossRefModal.value}
                            isOpen={!!crossRefModal}
                            onClose={() => setCrossRefModal(null)}
                        />
                    )
                }
            </div >

            <ScripturalConnectionLines
                key={passageId}
                containerRef={containerRef}
                isVisible={sortedAllusionsAndArcs.length > 0}
                connectionPoints={connectionPoints}
                passageId={passageId}
                isTransitionAnimating={isTransitionAnimating}
            />
        </div >
    );
}

